import { SyntheticEvent } from "react";
import {
  GridColumnMenuProps,
  GridPinnedColumnPosition,
  ColumnMenuPropsOverrides,
  useGridApiContext,
} from "@mui/x-data-grid-premium";
import { Box, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  SortByAlpha as SortIcon,
  FilterAlt as FilterIcon,
  PushPin as PinIcon,
  VisibilityOff as HideIcon,
  AccountTree as GroupIcon,
  RemoveCircleOutline as UngroupIcon,
} from "@mui/icons-material";

export function CustomColumnMenu(
  props: GridColumnMenuProps & ColumnMenuPropsOverrides
) {
  const {
    hideMenu,
    colDef,
    showSortAscending = true,
    showSortDescending = true,
    showFilter = true,
    showHideColumn = true,
    showPinColumn = false,
    showGrouping = false,
  } = props;

  const apiRef = useGridApiContext();

  // Check if this column is currently grouped
  const rowGroupingModel =
    (apiRef.current?.state as any)?.rowGrouping?.model || [];
  const isColumnGrouped = rowGroupingModel.includes(colDef.field);

  const handleSortAscending = (event: SyntheticEvent<Element, Event>) => {
    apiRef.current?.sortColumn(colDef.field, "asc");
    hideMenu(event);
  };

  const handleSortDescending = (event: SyntheticEvent<Element, Event>) => {
    apiRef.current?.sortColumn(colDef.field, "desc");
    hideMenu(event);
  };

  const handleShowFilter = (event: SyntheticEvent<Element, Event>) => {
    apiRef.current?.showFilterPanel(colDef.field);
    hideMenu(event);
  };

  const handleHideColumn = (event: SyntheticEvent<Element, Event>) => {
    if (props.onHideColumn) {
      props.onHideColumn(colDef.field);
    }
    hideMenu(event);
  };

  const handlePinColumn = (event: SyntheticEvent<Element, Event>) => {
    const currentPinned = apiRef.current?.isColumnPinned(colDef.field);
    if (currentPinned) {
      apiRef.current?.unpinColumn(colDef.field);
    } else {
      apiRef.current?.pinColumn(colDef.field, GridPinnedColumnPosition.LEFT);
    }
    hideMenu(event);
  };

  const handleGroupByColumn = (event: SyntheticEvent<Element, Event>) => {
    // Add this column to the grouping model
    const currentModel =
      (apiRef.current?.state as any)?.rowGrouping?.model || [];
    if (!currentModel.includes(colDef.field)) {
      apiRef.current?.setRowGroupingModel([...currentModel, colDef.field]);
    }
    hideMenu(event);
  };

  const handleUngroupColumn = (event: SyntheticEvent<Element, Event>) => {
    // Remove this column from the grouping model
    const currentModel =
      (apiRef.current?.state as any)?.rowGrouping?.model || [];
    apiRef.current?.setRowGroupingModel(
      currentModel.filter((field: string) => field !== colDef.field)
    );
    hideMenu(event);
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      {showSortAscending && colDef.sortable !== false && (
        <MenuItem onClick={handleSortAscending}>
          <ListItemIcon>
            <SortIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sort Ascending</ListItemText>
        </MenuItem>
      )}

      {showSortDescending && colDef.sortable !== false && (
        <MenuItem onClick={handleSortDescending}>
          <ListItemIcon>
            <SortIcon fontSize="small" sx={{ transform: "rotate(180deg)" }} />
          </ListItemIcon>
          <ListItemText>Sort Descending</ListItemText>
        </MenuItem>
      )}

      {showFilter && colDef.filterable !== false && (
        <MenuItem onClick={handleShowFilter}>
          <ListItemIcon>
            <FilterIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Filter</ListItemText>
        </MenuItem>
      )}

      {showHideColumn && colDef.hideable !== false && (
        <MenuItem onClick={handleHideColumn}>
          <ListItemIcon>
            <HideIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Hide Column</ListItemText>
        </MenuItem>
      )}

      {showPinColumn && (
        <MenuItem onClick={handlePinColumn}>
          <ListItemIcon>
            <PinIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {apiRef.current?.isColumnPinned(colDef.field)
              ? "Unpin Column"
              : "Pin Column"}
          </ListItemText>
        </MenuItem>
      )}

      {showGrouping && !isColumnGrouped && colDef.groupable !== false && (
        <MenuItem onClick={handleGroupByColumn}>
          <ListItemIcon>
            <GroupIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Group by this column</ListItemText>
        </MenuItem>
      )}

      {showGrouping && isColumnGrouped && (
        <MenuItem onClick={handleUngroupColumn}>
          <ListItemIcon>
            <UngroupIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Stop grouping by this column</ListItemText>
        </MenuItem>
      )}
    </Box>
  );
}
